import React, { useCallback, useRef, useState } from 'react';
import { InputSearchDropdownOption } from '@ibe/components/dist/ISO/Form/types/types';
import useThgConfig from '../../../Hooks/useThgConfig';
import { ConfigModel, LoggerFactory } from '@ibe/services';

const logger = LoggerFactory.get('GooglePlacesAutocomplete');

const useGooglePlaces = (config?: ConfigModel) => {
  const thgConfig = useThgConfig();
  const API_KEY = thgConfig.googlePlacesApiKey;

  const [placesLib, setPlacesLib] = useState<google.maps.PlacesLibrary | null>(null);
  const [token, setToken] = useState<google.maps.places.AutocompleteSessionToken | null>(null);

  // Refs so the stable throttled function always reads the latest state values
  const placesLibRef = useRef(placesLib);
  const tokenRef = useRef(token);
  placesLibRef.current = placesLib;
  tokenRef.current = token;

  const loadGooglePlacesScript = useCallback(async () => {
    if (!API_KEY) {
      logger.log('Google Places API Key not set');
      return;
    }

    if (!document.querySelector('script[data-google-maps]')) {
      const script = document.createElement('script');
      script.setAttribute('data-google-maps', 'true');
      script.innerHTML = `
        (g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=\`https://maps.\${c}apis.com/maps/api/js?\`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
          key: "${API_KEY}",
          v: "weekly",
        });
      `;
      document.head.appendChild(script);
    }

    const lib = (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary;
    setPlacesLib(lib);
    setToken(new lib.AutocompleteSessionToken());
  }, []);

  // gets autocomplete suggestions from google, returns proper InputSearchDropdownOptions
  const searchGooglePlaces = async (query: string) => {
    if (!placesLibRef.current || !query || !tokenRef.current) return [];

    const request: google.maps.places.AutocompleteRequest = {
      input: query,
      language: 'de-DE',
      region: 'de',
      includedPrimaryTypes: ['street_address', 'route', 'premise', 'subpremise'],
      sessionToken: tokenRef.current!
    };

    if (config?.tenant) {
      request.includedRegionCodes = [config?.tenant];
    }

    let suggestions: google.maps.places.AutocompleteSuggestion[] = [];
    try {
      const {
        suggestions: suggestionsTemp
      } = await placesLibRef.current.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      suggestions = suggestionsTemp;
    } catch (e) {
      logger.error(`Failed to fetch autocomplete suggestions: ${e}`);
    }

    return suggestions.map(suggestion => {
      const mainText = suggestion.placePrediction?.mainText?.text ?? '';
      const fullText = suggestion.placePrediction?.text.text ?? '';

      return {
        code: suggestion.placePrediction?.placeId ?? '',
        displayValue: mainText || fullText,
        label: (fullText as unknown) as React.ReactElement,
        indexer: suggestion.placePrediction?.placeId,
        fullObject: suggestion.placePrediction?.toPlace()
      } as InputSearchDropdownOption;
    });
  };

  // fetches the address parts from a place, since we only get simple infos from suggestions
  const getAddressFromGooglePlace = async (place: google.maps.places.Place) => {
    await place.fetchFields({ fields: ['addressComponents'] });

    // Generate new token, because google says the old one is now invalid
    setToken(new placesLib!.AutocompleteSessionToken());

    return {
      street: getAddressPart({ addressComponents: place.addressComponents, type: 'route' })
        ?.longText,
      houseNumber: getAddressPart({
        addressComponents: place.addressComponents,
        type: 'street_number'
      })?.longText,
      city: getAddressPart({ addressComponents: place.addressComponents, type: 'locality' })
        ?.longText,
      zip: getAddressPart({ addressComponents: place.addressComponents, type: 'postal_code' })
        ?.longText,
      country: getAddressPart({ addressComponents: place.addressComponents, type: 'country' })
        ?.shortText,
      all: place.addressComponents
    };
  };

  const getAddressPart = ({
    addressComponents,
    type
  }: {
    addressComponents: google.maps.places.AddressComponent[] | undefined;
    type: string;
  }) => {
    return addressComponents?.find(component => component.types.includes(type));
  };

  return {
    loadGooglePlacesScript,
    searchGooglePlaces,
    getAddressFromGooglePlace
  };
};

export default useGooglePlaces;
